import { memo, useCallback, useEffect } from 'react'
import { Menu, X, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { CartIcon } from '@/components/features/Cart/CartIcon'
import { CartDropdown } from '@/components/features/Cart/CartDropdown'
import { useCart } from '@/stores/useCart'

interface HeaderActionsProps {
  onCloseMenu: () => void
  isMenuOpen?: boolean
  onToggleMenu?: () => void
}

const HeaderActions = memo(
  ({
    onCloseMenu: _onCloseMenu,
    isMenuOpen = false,
    onToggleMenu,
  }: HeaderActionsProps) => {
    const { setIsOpen } = useCart()

    // Close cart when mobile menu opens
    useEffect(() => {
      if (isMenuOpen) {
        setIsOpen(false)
      }
    }, [isMenuOpen, setIsOpen])

    const handleToggleMenu = useCallback(() => {
      onToggleMenu?.()
    }, [onToggleMenu])

    return (
      <div className="flex items-center space-x-4 xl:space-x-6">
        {/* Search - Hidden on mobile, shown on larger screens */}
        <div className="hidden md:block relative w-80">
          <div className="flex items-center bg-gradient-to-r from-brand-purple/10 via-brand-pink/10 to-brand-blue/10 backdrop-blur-sm border border-brand-purple/30 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 focus-within:ring-2 focus-within:ring-brand-purple/30 focus-within:border-brand-purple/50">
            <div className="pl-4 pr-3">
              <Search className="h-4 w-4 text-muted-foreground" />
            </div>
            <Input
              type="text"
              placeholder="Produkte suchen..."
              className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/60 flex-1 h-10"
            />
          </div>
        </div>

        {/* Cart */}
        <div className="flex items-center">
          <CartIcon />
          <CartDropdown />
        </div>

        {/* Mobile Menu Toggle */}
        {onToggleMenu && (
          <button
            onClick={handleToggleMenu}
            className="xl:hidden flex items-center justify-center w-10 h-10 text-gray-600 hover:text-brand-purple transition-all duration-300 hover:scale-110 active:scale-95"
            aria-label={isMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
          >
            <div className="relative w-5 h-5">
              <Menu
                className={`absolute inset-0 w-5 h-5 transition-all duration-300 ${
                  isMenuOpen
                    ? 'opacity-0 rotate-180 scale-0'
                    : 'opacity-100 rotate-0 scale-100'
                }`}
              />
              <X
                className={`absolute inset-0 w-5 h-5 transition-all duration-300 ${
                  isMenuOpen
                    ? 'opacity-100 rotate-0 scale-100'
                    : 'opacity-0 rotate-180 scale-0'
                }`}
              />
            </div>
          </button>
        )}
      </div>
    )
  }
)

HeaderActions.displayName = 'HeaderActions'

export default HeaderActions
